/**
 * CachedMetadataRow — static (non-interactive) metadata row rendered from
 * cached data while the real ProjectMetadata loads from Dexie.
 *
 * Mirrors the visual layout of ProjectMetadata (When + Deadline + Tags)
 * but without click handlers or live queries.
 */

import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { cn } from "@/shared/utils/general/cn"
import { StartAtType, StartAtSubType } from "@/db/schema"
import type { CachedContainerMeta } from "@/features/views/loadingPlaceholders"
import DeadlineIcon from "@/shared/icons/totodoicons/stroke/deadline.svg?react"
import MoonIcon from "@/shared/icons/totodoicons/stroke/moon.svg?react"
import CalendarUpcomingIcon from "@/shared/icons/totodoicons/stroke/calendar-upcoming.svg?react"
import StarIcon from "@/shared/icons/totodoicons/stroke/star.svg?react"
import CalendarTodayIcon from "@/shared/icons/totodoicons/stroke/calendar-today.svg?react"
import TagIcon from "@/shared/icons/totodoicons/stroke/tag.svg?react"
import { parseDbDate } from "@totodo/operations"

interface CachedMetadataRowProps {
  meta: CachedContainerMeta
}

export function CachedMetadataRow({ meta }: CachedMetadataRowProps) {
  const { t } = useTranslation()

  const startAtInfo = useMemo(() => {
    if (!meta.startAt) return null

    const startDate = dayjs(meta.startAt)
    const today = dayjs()
    const isSomeday = meta.startAtType === StartAtType.Someday
    const isEveningTask = meta.startAtSubType === StartAtSubType.ThisEvening

    let label: string
    let icon: React.ReactNode
    let colorClass: string

    if (isSomeday) {
      label = t("Someday")
      colorClass = "text-purple-400"
      icon = <CalendarUpcomingIcon className={cn("size-4", colorClass)} />
    } else if (isEveningTask) {
      label = startDate.isSame(today, "day")
        ? t("This evening")
        : startDate.isSame(today.add(1, "day"), "day")
          ? t("Tomorrow evening")
          : t("{{date}} evening", {
              date: startDate.format("ddd, DD MMM").toLowerCase(),
            })
      colorClass = "text-blue-400"
      icon = <MoonIcon className={cn("size-4", colorClass)} />
    } else if (startDate.isSame(today, "day")) {
      label = t("Today")
      colorClass = "text-yellow-500"
      icon = <StarIcon className={cn("size-4 fill-yellow-500", colorClass)} />
    } else if (startDate.isBefore(today, "day")) {
      const daysAgo = today.diff(startDate, "day")
      label =
        daysAgo === 1
          ? t("Started yesterday")
          : t("Started {{count}} days ago", { count: daysAgo })
      colorClass = "text-yellow-600 opacity-60"
      icon = <StarIcon className={cn("size-4 fill-yellow-600", colorClass)} />
    } else if (startDate.isSame(today.add(1, "day"), "day")) {
      label = t("Tomorrow")
      colorClass = "text-primary"
      icon = <CalendarTodayIcon className={cn("size-4", colorClass)} />
    } else {
      const dayName = startDate.format("ddd").toLowerCase()
      const dayNumber = startDate.format("DD")
      const monthName = startDate.format("MMM").toLowerCase()
      label = `${dayName}, ${dayNumber} ${monthName}`
      colorClass = "text-primary"
      icon = <CalendarTodayIcon className={cn("size-4", colorClass)} />
    }

    return { label, icon }
  }, [meta.startAt, meta.startAtType, meta.startAtSubType, t])

  const dueAtInfo = useMemo(() => {
    const dueMs = parseDbDate(meta.dueAt)
    if (dueMs === undefined) return null

    const dueDate = dayjs(dueMs)
    const today = dayjs()
    const isOverdue = dueDate.isBefore(today, "day")
    const isDueToday = dueDate.isSame(today, "day")

    const dayName = dueDate.format("ddd").toLowerCase()
    const dayNumber = dueDate.format("DD")
    const monthName = dueDate.format("MMM").toLowerCase()
    const dateLabel = `${dayName}, ${dayNumber} ${monthName}`

    let daysText: string | null = null
    if (isOverdue) {
      const daysOverdue = today.diff(dueDate, "day")
      daysText =
        daysOverdue === 1
          ? t("1 day overdue")
          : t("{{count}} days overdue", { count: daysOverdue })
    } else if (isDueToday) {
      daysText = t("due today")
    } else {
      const daysLeft = dueDate.diff(today, "day")
      daysText =
        daysLeft === 1
          ? t("1 day left")
          : t("{{count}} days left", { count: daysLeft })
    }

    return { dateLabel, daysText, isOverdue, isDueToday }
  }, [meta.dueAt, t])

  const tags = meta.tags
  const hasTags = tags && tags.length > 0
  const hasAnyMetadata = startAtInfo || dueAtInfo || hasTags

  if (!hasAnyMetadata) return null

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 -ml-1" aria-hidden>
      {/* When / Schedule */}
      {startAtInfo && (
        <span className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-sm font-medium">
          {startAtInfo.icon}
          <span className="truncate">{startAtInfo.label}</span>
        </span>
      )}

      {/* Deadline */}
      {dueAtInfo && (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-sm",
            dueAtInfo.isOverdue
              ? "font-semibold text-destructive"
              : dueAtInfo.isDueToday
                ? "text-destructive"
                : "text-foreground",
          )}
        >
          <DeadlineIcon
            className={cn(
              "size-4",
              dueAtInfo.isOverdue ? "text-destructive" : "text-red-500",
            )}
          />
          <span className="truncate">{dueAtInfo.dateLabel}</span>
          {dueAtInfo.daysText && (
            <span className="text-xs text-muted-foreground/60">
              {dueAtInfo.daysText}
            </span>
          )}
        </span>
      )}

      {/* Tags */}
      {hasTags && (
        <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-sm text-muted-foreground">
          <TagIcon className="size-3.5" />
          <span className="flex items-center gap-1">
            {tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex max-w-24 truncate rounded-full bg-secondary/50 px-2 py-px text-xs font-medium text-secondary-foreground/70"
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="text-xs text-muted-foreground/60">
                +{tags.length - 3}
              </span>
            )}
          </span>
        </span>
      )}
    </div>
  )
}
